// Fetches (and, on the backend, generates+permanently stores) a shop's QR
// code — same "made once, shown from then on" idea as the static KalKode QR
// image, just per-shop. Session-scoped client cache on top so re-opening the
// overlay for a shop already seen this session never re-hits the network.
const qrCache = new Map();

export const getShopPublicUrl = (shopId) => `https://kalkode.com/shop/${shopId}/view`;

// Same provider UnifiedCartWindow already uses for its payment QR — used here
// as a last-resort client-side fallback so the overlay always shows a working
// QR even if the backend endpoint is unreachable (e.g. running the CRA dev
// server without the Vercel functions, or a transient network hiccup). It
// just won't be the permanently-stored one until a real request succeeds.
const liveQrFallback = (shopId) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=400x400&margin=12&data=${encodeURIComponent(getShopPublicUrl(shopId))}`;

export const getShopQrCode = async (shopId) => {
  if (qrCache.has(shopId)) return qrCache.get(shopId);

  try {
    // Routed through guest-operations.js's operation dispatcher rather than
    // its own api/ file — Vercel's function count is capped and already at
    // the limit, so this reuses the existing endpoint instead of adding one.
    const response = await fetch('/api/guest-operations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ operation: 'get_shop_qrcode', shopId }),
    });

    if (!response.ok) throw new Error(`QR endpoint returned ${response.status}`);

    const data = await response.json();
    if (!data?.qrCodeUrl) throw new Error('QR endpoint returned no url');

    // Only cache persisted results — an unpersisted fallback from the backend
    // (or this catch below) should be retried next time in case the real
    // one is ready by then.
    if (data.persisted !== false) {
      qrCache.set(shopId, data.qrCodeUrl);
    }
    return data.qrCodeUrl;
  } catch (err) {
    console.warn('Shop QR endpoint unreachable, using live fallback:', err.message);
    return liveQrFallback(shopId);
  }
};
